import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult
} from '@tanstack/react-query'
import type {
  AlertRuleDto,
  AlertRuleListQuery,
  AuditEntryDto,
  AuditQuery,
  BotDetailDto,
  BotDto,
  BotEventDto,
  BotListQuery,
  DashboardSummaryDto,
  McAccountDto,
  OrgCurrentDto,
  Paginated,
  ServerDto,
  SystemInfoDto
} from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'

/**
 * Чтение серверных данных. Обновления приходят по WS и патчат кэш точечно
 * (docs/06 §9), поэтому polling тут не нужен: `refetchInterval` нигде нет.
 */

export function useDashboardSummary(): UseQueryResult<DashboardSummaryDto> {
  return useQuery({ queryKey: qk.dashboard.summary, queryFn: () => api.dashboard.summary() })
}

export function useBots(query: BotListQuery = {}): UseQueryResult<Paginated<BotDto>> {
  return useQuery({ queryKey: qk.bots.list(query), queryFn: () => api.bots.list(query) })
}

export function useBot(botId: string): UseQueryResult<BotDetailDto> {
  return useQuery({ queryKey: qk.bots.detail(botId), queryFn: () => api.bots.get(botId) })
}

/**
 * История логов. Живые строки дописывает WS-редьюсер в **этот же** ключ,
 * поэтому фильтрация уровня/поиска делается на клиенте, а не в queryKey —
 * иначе каждая смена фильтра выбрасывала бы уже полученные live-строки.
 */
export function useBotEvents(
  botId: string,
  limit = 200,
  /**
   * Экран логов выбирает бота сам и до загрузки списка ботов не знает, какого.
   * Пустой `botId` не должен превращаться в запрос к `/bots//events`.
   */
  enabled = true
): UseQueryResult<Paginated<BotEventDto>> {
  return useQuery({
    queryKey: qk.bots.events(botId),
    queryFn: () => api.bots.events(botId, { limit }),
    enabled: enabled && botId !== ''
  })
}

export function useMcAccounts(): UseQueryResult<Paginated<McAccountDto>> {
  return useQuery({ queryKey: qk.mcAccounts.list, queryFn: () => api.mcAccounts.list() })
}

export function useServers(): UseQueryResult<Paginated<ServerDto>> {
  return useQuery({ queryKey: qk.servers.list, queryFn: () => api.servers.list() })
}

export function useSystemInfo(): UseQueryResult<SystemInfoDto> {
  return useQuery({ queryKey: qk.system.info, queryFn: () => api.system.info() })
}

/** Текущая организация: лимиты и использование для экрана настроек. */
export function useOrgCurrent(): UseQueryResult<OrgCurrentDto> {
  return useQuery({ queryKey: qk.org.current, queryFn: () => api.org.current() })
}

/** Тип страницы аудита — вынесен, чтобы подпись хука читалась. */
export type AuditPages = { pages: Paginated<AuditEntryDto>[]; pageParams: unknown[] }

/**
 * Аудит с курсорной пагинацией («показать ещё», docs/06 §1).
 *
 * `useInfiniteQuery`, а не ручной массив страниц: он сам хранит `nextCursor`,
 * умеет `isFetchingNextPage` и не теряет уже загруженные страницы при
 * ререндере. Фильтры входят в ключ — смена фильтра начинает выборку заново,
 * это здесь правильное поведение (в отличие от лога, куда дописывает WS).
 */
export function useAudit(query: AuditQuery = {}): UseInfiniteQueryResult<AuditPages, Error> {
  return useInfiniteQuery({
    queryKey: qk.audit.list(query),
    queryFn: ({ pageParam }) =>
      api.audit.list({
        ...query,
        ...(typeof pageParam === 'string' ? { cursor: pageParam } : {})
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Paginated<AuditEntryDto>) => last.nextCursor
  })
}

/**
 * Правила алертов (docs/06 §7.1).
 *
 * `enabled` — не фильтр экрана, а выключатель самого запроса: `/alerts`
 * доступен только OWNER/ADMIN, и у OPERATOR'а он вернёт 403. Ходить туда,
 * заведомо зная про 403, значит писать в аудит чужие отказы и рисовать
 * пользователю ошибку на пустом месте.
 */
export function useAlerts(
  query: AlertRuleListQuery = {},
  enabled = true
): UseQueryResult<Paginated<AlertRuleDto>> {
  return useQuery({
    queryKey: qk.alerts.list(query),
    queryFn: () => api.alerts.list(query),
    enabled
  })
}

export function useRecentEvents(): UseQueryResult<BotEventDto[]> {
  // Наполняется только из WS: REST-эндпоинта «события всей org» в docs/06 нет.
  return useQuery({
    queryKey: qk.dashboard.recentEvents,
    queryFn: () => Promise.resolve<BotEventDto[]>([]),
    staleTime: Number.POSITIVE_INFINITY
  })
}
